import { useCallback, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';

import { useGradeFlow } from '@/states/grade-flow';
import { useSaveNewInsightsGrade } from '@/hooks/grade-flow/use-save-new-insights-grade';

import { Button } from '@/components/ui/button';
import { Signature } from '@/components/signature';
import { MultiDecisionButtons } from '@/components/form/multi-decision-buttons';
import { Hr } from '@/components/ui/divider';
import { FooterButtons, LayoutStep } from '@/components/grading/grade-flow/layouts-flow';
import { GoBackStep } from '@/components/grading/grade-flow/go-back-step-btn';
import { OtherDiseaseQuestion } from '@/components/grading/grade-flow/other-disease';
import { GradeUrgentFollowUp } from '@/components/grading/grade-flow/urgent-follow-up';
import { ICDCodesSelect } from '@/components/grading/grade-flow/icd-select';
import { InsightsThumbnails } from '@/components/insights/insights-thumbnails';

export function DrInsightsStep2() {
  const { t } = useTranslation();

  const { onSubmitGradeCase } = useSaveNewInsightsGrade();
  const { isSavingGrade, form, setReferralDrFollowUp, setSignature } = useGradeFlow(
    useShallow((s) => ({
      isSavingGrade: s.isSavingGrade,
      setReferralDrFollowUp: s.setReferralDrFollowUp,
      setSignature: s.setSignature,
      form: s.form,
    }))
  );

  const handleSelectFollowUp = useCallback(
    (selected: string) => {
      setReferralDrFollowUp(selected);
    },
    [setReferralDrFollowUp]
  );

  const handleSignatureChange = useCallback(
    (signature: string) => {
      setSignature(signature);
    },
    [setSignature]
  );

  const isSaveBlocked = useMemo(() => {
    return !form?.signature_data_uri;
  }, [form]);

  const OPTIONS_REFERRAL = useMemo(
    () => [
      { label: t('grade-options-referral.now'), value: '0_months' },
      { label: t('grade-options-referral.return-6-to-12-mo'), value: '6-12_months' },
      { label: t('grade-options-referral.return-12-mo'), value: '12_months' },
    ],
    [t]
  );

  return (
    <LayoutStep>
      <InsightsThumbnails size="sm" />
      <div className="flex flex-col gap-12 my-12">
        <MultiDecisionButtons
          disabled={isSavingGrade}
          label={t('grade-question-referral')}
          onSelection={handleSelectFollowUp}
          selected={form?.referral_dr_follow_up ? [form?.referral_dr_follow_up] : []}
          options={OPTIONS_REFERRAL}
          helper={{
            text: t('grade-question-referral-helper'),
            link: 'https://grades.vitazi.ai/Diabetic%20Retinopathy%20PPP_2022%20Update.pdf#page=25',
          }}
        />
        <OtherDiseaseQuestion />
        <GradeUrgentFollowUp />
        <ICDCodesSelect />
      </div>
      <Hr className="col-span-2 mt-8" />
      <h4 className="text-base/5 font-medium text-gray-800 mt-8 mb-4 text-center">
        {t('grade-question-signature')}
      </h4>
      <Signature
        onChange={handleSignatureChange}
        initialSignature={form?.signature_data_uri}
        disabled={isSavingGrade}
      />

      <FooterButtons>
        <GoBackStep backTo={'INSIGHTS_DR_FORM_STEP_1'} />
        <Button onClick={onSubmitGradeCase} disabled={isSaveBlocked} loading={isSavingGrade}>
          {t('Submit')}
        </Button>
      </FooterButtons>
    </LayoutStep>
  );
}
